<!-- HotX Console 0.1.121 仪表盘真实流量。实际修改时间：2026-09-29 13:25:25 CST；精确父基线：2b265b8475709018e4bf7099ec6a081871eb4c6b；变更：CONSOLE-HOME-DATA-PASSWORD-20260929。 -->
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import EmptyState from '@hotx-cdn/shared/components/EmptyState.vue';
import { consoleApi, hasConsoleSession } from '../api/client';
import type { AccessSummary, PortalOverview, TrafficPoint } from '../api/types';
import { usePointStore } from '../stores/points';

const loading = ref(false);
const error = ref('');
const trafficError = ref('');
const feedback = ref('');
const overview = ref<PortalOverview | null>(null);
const accessSummary = ref<AccessSummary | null>(null);
const points = usePointStore();
const pointSummary = computed(() => points.summary);

const traffic = computed<TrafficPoint | undefined>(() => {
  const hourly = accessSummary.value?.hourly ?? [];
  return [...hourly].sort((left, right) => {
    const leftTime = Date.parse(left.bucketStart || '');
    const rightTime = Date.parse(right.bucketStart || '');
    return (Number.isFinite(leftTime) ? leftTime : 0) - (Number.isFinite(rightTime) ? rightTime : 0);
  }).at(-1);
});
const currentBandwidthBps = computed(() => pointBandwidth(traffic.value));
const domainStatuses = computed(() => Object.entries(overview.value?.domainStatusCounts ?? {}));
const planName = computed(() => display(overview.value?.serviceStatus?.planName));
const quotaSummary = computed(() => {
  const status = overview.value?.serviceStatus;
  if (!status) {
    return '域名额度等待套餐生效';
  }
  const limit = status.rootDomainLimit === undefined || status.rootDomainLimit === null
    ? '不限'
    : formatNumber(status.rootDomainLimit);
  return `已用 ${formatNumber(status.rootDomainUsed)} / ${limit}，剩余 ${formatNumber(status.rootDomainRemaining)}`;
});

onMounted(() => {
  if (!hasConsoleSession()) return;
  load();
  points.refresh().catch(() => undefined);
});

async function load(showFeedback = false) {
  loading.value = true;
  error.value = '';
  trafficError.value = '';
  if (showFeedback) {
    feedback.value = '仪表盘刷新中';
  }
  const now = new Date();
  const from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const [overviewResult, trafficResult] = await Promise.allSettled([
    consoleApi.portal.overview(),
    consoleApi.logs.access({ from: from.toISOString(), to: now.toISOString(), granularity: '1h' })
  ]);
  try {
    if (overviewResult.status === 'rejected') throw overviewResult.reason;
    overview.value = overviewResult.value;
    if (showFeedback) {
      feedback.value = '仪表盘已刷新';
    }
  } catch (e) {
    overview.value = null;
    feedback.value = '';
    error.value = e instanceof Error ? e.message : '总览加载失败';
  }
  if (trafficResult.status === 'fulfilled') {
    accessSummary.value = trafficResult.value;
  } else {
    accessSummary.value = null;
    trafficError.value = trafficResult.reason instanceof Error
      ? trafficResult.reason.message
      : '流量数据加载失败';
  }
  loading.value = false;
}

function formatNumber(value: unknown) {
  return typeof value === 'number' ? value.toLocaleString('zh-CN') : display(value);
}

function formatBytes(value: unknown) {
  if (typeof value !== 'number') {
    return display(value);
  }
  if (value >= 1024 ** 4) {
    return `${(value / 1024 ** 4).toFixed(2)} TB`;
  }
  if (value >= 1024 ** 3) {
    return `${(value / 1024 ** 3).toFixed(2)} GB`;
  }
  if (value >= 1024 ** 2) {
    return `${(value / 1024 ** 2).toFixed(2)} MB`;
  }
  return `${value.toLocaleString('zh-CN')} B`;
}

function pointBandwidth(point?: TrafficPoint) {
  if (!point) return undefined;
  if (typeof point.bandwidthBps === 'number' && Number.isFinite(point.bandwidthBps)) {
    return Math.max(0, point.bandwidthBps);
  }
  if (typeof point.bodyBytesSent === 'number' && Number.isFinite(point.bodyBytesSent)) {
    const seconds = typeof point.bucketSeconds === 'number'
      && Number.isFinite(point.bucketSeconds)
      && point.bucketSeconds > 0
      ? point.bucketSeconds
      : 3600;
    return Math.max(0, point.bodyBytesSent * 8 / seconds);
  }
  return undefined;
}

function formatBandwidth(value: unknown) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return display(value);
  if (value === 0) return '0 bps';
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)} Gbps`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)} Mbps`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(2)} Kbps`;
  return `${value.toFixed(0)} bps`;
}

function formatPercent(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(1)}%` : display(value);
}

function display(value: unknown) {
  return value === undefined || value === null || value === '' ? '暂无数据' : String(value);
}

function serviceStatusText(value: unknown) {
  const mapping: Record<string, string> = {
    RUNNING: '服务运行中',
    ACTIVE: '服务正常',
    ENABLED: '服务正常',
    PAUSED: '服务已暂停',
    SUSPENDED: '服务已暂停',
    EXPIRED: '套餐已过期',
    NO_PLAN: '未开通套餐',
    DISABLED: '服务未启用'
  };
  return typeof value === 'string' ? mapping[value] || value : display(value);
}

function domainStatusText(value: string) {
  const mapping: Record<string, string> = {
    ENABLED: '运行正常',
    ACTIVE: '运行正常',
    RUNNING: '运行正常',
    PENDING_DNS: '等待接入',
    WAITING_DNS: '等待接入',
    CREATED: '等待接入',
    CHECKING: '正在检测',
    FAILED: '配置异常',
    CONFIG_ERROR: '配置异常',
    ERROR: '配置异常',
    PAUSED: '已暂停',
    SUSPENDED: '已暂停',
    DISABLED: '已暂停'
  };
  return mapping[value] || value;
}
</script>

<template>
  <section class="page">
    <RouterLink v-if="pointSummary?.newcomerRewardPending || !pointSummary?.todaySigned" to="/points" class="point-welcome-strip">
      <span class="point-welcome-mark">P</span>
      <span>
        <b>{{ pointSummary?.newcomerRewardPending ? '新人积分待领取' : '今日签到待完成' }}</b>
        <small>{{ pointSummary?.newcomerRewardPending ? '首次签到可同时获得新人奖励和签到奖励' : '前往积分中心签到并查看本周记录' }}</small>
      </span>
      <strong>去签到 →</strong>
    </RouterLink>
    <div class="page-title dashboard-title">
      <div>
        <h1>仪表盘</h1>
        <p>展示 overview API 返回的最新客户、服务、域名和流量数据。</p>
      </div>
      <div class="dashboard-title-actions">
        <RouterLink
          class="dashboard-free-cdn-link"
          data-test="dashboard-apply-free-cdn"
          :to="{ path: '/domains', query: { applyFreeCdn: '1' } }"
        >
          申请免费 CDN
        </RouterLink>
        <button type="button" data-test="refresh-dashboard" :disabled="loading" @click="load(true)">
          {{ loading ? '刷新中' : '刷新' }}
        </button>
      </div>
    </div>

    <el-alert v-if="error" :title="error" type="error" show-icon />
    <el-alert v-if="trafficError" :title="trafficError" type="warning" show-icon />
    <el-alert v-if="feedback" :title="feedback" type="success" show-icon />
    <el-skeleton v-if="loading" :rows="5" animated />
    <EmptyState v-else-if="!overview" description="登录后将展示真实业务总览。" />

    <template v-else>
      <div class="metric-grid">
        <el-card>
          <span>域名数量</span>
          <strong>{{ formatNumber(overview.domainCount) }}</strong>
        </el-card>
        <el-card>
          <span>服务状态</span>
          <strong>{{ serviceStatusText(overview.serviceStatus?.serviceStatus) }}</strong>
        </el-card>
        <el-card>
          <span>套餐</span>
          <strong>{{ display(overview.serviceStatus?.planName) }}</strong>
        </el-card>
        <el-card>
          <span>业务写权限</span>
          <strong>{{ overview.writeAccess?.canModifyBusiness ?? overview.serviceStatus?.canModifyBusiness ? '允许保存' : '禁止保存' }}</strong>
        </el-card>
        <el-card>
          <span>最新请求量</span>
          <strong data-test="dashboard-latest-requests">{{ formatNumber(traffic?.requestCount) }}</strong>
        </el-card>
        <el-card>
          <span>最新流量</span>
          <strong data-test="dashboard-latest-bytes">{{ formatBytes(traffic?.bodyBytesSent) }}</strong>
        </el-card>
        <el-card>
          <span>当前带宽</span>
          <strong data-test="dashboard-current-bandwidth">{{ formatBandwidth(currentBandwidthBps) }}</strong>
        </el-card>
        <el-card>
          <span>缓存命中率</span>
          <strong data-test="dashboard-cache-hit-rate">{{ formatPercent(traffic?.cacheHitRatePercent) }}</strong>
        </el-card>
      </div>

      <section class="dashboard-plan-strip" data-test="dashboard-plan-strip">
        <div>
          <span>当前套餐</span>
          <strong>{{ planName }}</strong>
          <p>{{ quotaSummary }}</p>
        </div>
        <RouterLink
          class="dashboard-plan-action"
          :to="{ path: '/domains', query: { applyFreeCdn: '1' } }"
        >
          申请免费 CDN
        </RouterLink>
      </section>

      <section class="dashboard-panel">
        <h2>域名状态</h2>
        <div v-if="domainStatuses.length" class="status-list">
          <div v-for="[status, count] in domainStatuses" :key="status">
            <span>{{ domainStatusText(status) }}</span>
            <strong>{{ formatNumber(count) }}</strong>
          </div>
        </div>
        <EmptyState v-else title="暂无域名状态数据" />
      </section>

      <section class="dashboard-panel">
        <h2>服务提示</h2>
        <p>{{ display(overview.serviceStatus?.notice) }}</p>
        <p>根域名已用 {{ formatNumber(overview.serviceStatus?.rootDomainUsed) }}，剩余 {{ formatNumber(overview.serviceStatus?.rootDomainRemaining) }}</p>
        <p>最后更新：{{ display(traffic?.bucketStart ?? overview.lastUpdatedAt) }}</p>
      </section>
    </template>
  </section>
</template>

<style scoped>
.point-welcome-strip {
  display: grid;
  grid-template-columns: 42px 1fr auto;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border: 1px solid #92d5c7;
  border-radius: 10px;
  color: #174d43;
  background: linear-gradient(110deg, #effbf7, #e2f5f0);
  text-decoration: none;
}

.point-welcome-strip span:nth-child(2) {
  display: grid;
  gap: 3px;
}

.point-welcome-strip small {
  color: #5c756e;
}

.point-welcome-strip > strong {
  color: #187467;
}

.point-welcome-mark {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 50%;
  color: #fff;
  background: #187467;
  font-weight: 900;
}

.dashboard-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.dashboard-title button {
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid #2f604c;
  border-radius: 8px;
  color: #fff;
  background: #2f604c;
  font-weight: 800;
  cursor: pointer;
}

.dashboard-title-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}

.dashboard-free-cdn-link,
.dashboard-plan-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  border: 1px solid #d69a37;
  border-radius: 8px;
  padding: 0 14px;
  color: #173625;
  background: #f6c56f;
  font-weight: 900;
  text-decoration: none;
  box-shadow: 0 10px 24px rgba(212, 137, 36, 0.18);
  animation: dashboard-free-cdn-pulse 2.8s ease-in-out infinite;
}

.dashboard-plan-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #d6eadf;
  border-radius: 8px;
  background: #fbfdfb;
}

.dashboard-plan-strip span {
  color: #63776d;
  font-size: 13px;
  font-weight: 800;
}

.dashboard-plan-strip strong {
  display: block;
  margin-top: 3px;
  color: #193429;
  font-size: 18px;
}

.dashboard-plan-strip p {
  margin: 3px 0 0;
  color: #52665c;
  font-size: 13px;
}

.dashboard-plan-action {
  flex: 0 0 auto;
  color: #fff;
  border-color: #123f2a;
  background: #123f2a;
  box-shadow: 0 12px 26px rgba(18, 63, 42, 0.18);
}

@keyframes dashboard-free-cdn-pulse {
  0%,
  100% {
    transform: translateY(0);
    box-shadow: 0 10px 24px rgba(212, 137, 36, 0.18), 0 0 0 0 rgba(212, 137, 36, 0.2);
  }

  50% {
    transform: translateY(-1px);
    box-shadow: 0 14px 30px rgba(212, 137, 36, 0.24), 0 0 0 8px rgba(212, 137, 36, 0);
  }
}

.dashboard-panel {
  padding: 16px;
  border: 1px solid #d9e4dc;
  border-radius: 8px;
  background: #fbfdfb;
}

.dashboard-panel h2 {
  margin: 0 0 12px;
  font-size: 18px;
}

.dashboard-panel p {
  margin: 8px 0 0;
  color: #52665c;
}

.status-list {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.status-list div {
  display: grid;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: #eef3ef;
}

@media (max-width: 720px) {
  .dashboard-title {
    flex-direction: column;
  }

  .dashboard-title-actions,
  .dashboard-plan-strip {
    width: 100%;
    align-items: stretch;
  }

  .dashboard-free-cdn-link,
  .dashboard-plan-action,
  .dashboard-title button {
    width: 100%;
  }

  .dashboard-plan-strip {
    flex-direction: column;
  }

  .status-list {
    grid-template-columns: 1fr;
  }
}
</style>
